import React from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 60px 0;
  background: linear-gradient(135deg, #2b3d5c 0%, #1a2538 100%);
  color: white;
  margin: 0 -20px 60px -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    margin: 0 -20px 40px -20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 0;
  opacity: 0.95;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Section = styled.section`
  margin-bottom: 60px;
  line-height: 1.8;
  font-size: 1.1rem;
  color: #333;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #2b3d5c;
  margin-bottom: 30px;
`;

const Quote = styled.blockquote`
  font-style: italic;
  border-left: 4px solid #0062cc;
  padding-left: 20px;
  margin: 30px 0;
  color: #555;
`;

const Highlight = styled.div`
  background: rgba(0, 98, 204, 0.1);
  border-radius: 12px;
  padding: 30px;
  margin: 30px 0;
  border: 2px solid rgba(0, 98, 204, 0.2);
`;

const HighlightTitle = styled.h3`
  color: #0062cc;
  margin-bottom: 15px;
  font-size: 1.3rem;
`;

const OurStory = () => {
  return (
    <>
      <Container>
        <HeroSection>
          <HeroTitle>Built by an LA Agent, For LA Agents</HeroTitle>
          <HeroSubtitle>
            Born from real experience with LA's complex tenant notification requirements
          </HeroSubtitle>
        </HeroSection>

        <Section>
          <SectionTitle>The Catalyst</SectionTitle>
          <p>
            After 20 years in Los Angeles real estate, I thought I had seen it all. Then came June 2025,
            and a routine LAHD investigation at 240 Robinson St changed everything.
          </p>
          <Quote>
            "Where's your proof of proper 24-hour notice? Email timestamps aren't enough anymore."
          </Quote>
          <p>
            That single question from an LAHD inspector made me realize how vulnerable agents and
            landlords are when it comes to proving compliance with tenant notification requirements.
            Even with diligent record-keeping, we lacked a systematic way to prove we had followed
            the law.
          </p>
        </Section>

        <Section>
          <SectionTitle>The Problem</SectionTitle>
          <p>
            Los Angeles has some of the strictest tenant protection laws in the country. A single
            missed notification or improper documentation can result in hefty fines and legal
            complications. The existing solutions were either:
          </p>
          <ul>
            <li>Generic scheduling tools that ignore compliance requirements</li>
            <li>Basic email systems with no proof of delivery</li>
            <li>Manual record-keeping that's prone to errors</li>
            <li>Expensive enterprise systems built for large property management companies</li>
          </ul>
          <p>
            What we needed was a specialized tool that understood the unique challenges of showing
            tenant-occupied properties in legally complex markets like Los Angeles.
          </p>
        </Section>

        <Highlight>
          <HighlightTitle>The LAHD Investigation</HighlightTitle>
          <p>
            When the LAHD inspector questioned our notification process at 240 Robinson St, we had
            emails, text messages, and even notes of phone calls. But what we lacked was a
            systematic, indisputable record that proved we had met every requirement of the law.
          </p>
          <p>
            That investigation, while ultimately resolved, revealed a critical gap in how we handle
            tenant notifications and maintain compliance records.
          </p>
        </Highlight>

        <Section>
          <SectionTitle>The Solution</SectionTitle>
          <p>
            ShowingBooker was built to solve this exact problem. We created a system that:
          </p>
          <ul>
            <li>Automatically sends legally compliant 24-hour notices</li>
            <li>Delivers notifications through multiple channels</li>
            <li>Creates an immutable record of all communication</li>
            <li>Generates court-admissible documentation</li>
            <li>Protects agents and landlords from liability</li>
          </ul>
          <p>
            Every feature was designed with LA's specific legal requirements in mind, ensuring
            you're protected in one of the country's most regulated rental markets.
          </p>
        </Section>

        <Section>
          <SectionTitle>Our Mission</SectionTitle>
          <p>
            ShowingBooker's mission is to provide bulletproof compliance protection for real estate
            professionals operating in legally complex markets. We believe that with the right
            tools, showing tenant-occupied properties doesn't have to be a legal minefield.
          </p>
          <Quote>
            "We're not just a showing management platform. We're your compliance shield in a
            complex legal landscape."
          </Quote>
        </Section>

        <Highlight>
          <HighlightTitle>Why LA Agents Trust Us</HighlightTitle>
          <ul>
            <li>Built specifically for LA's legal requirements</li>
            <li>Created by an agent with 20 years of local experience</li>
            <li>Focused exclusively on tenant-occupied property compliance</li>
            <li>Continuously updated to match changing regulations</li>
            <li>Proven track record with LAHD requirements</li>
          </ul>
        </Highlight>
      </Container>

      <Footer />
    </>
  );
};

export default OurStory;